// Shared UI primitives - thin wrappers over the index.css design tokens.
// They don't replicate the .card / .btn-* / .input / .pill CSS classes,
// which are used directly.

import { ChevronRight, Loader2, type LucideIcon, X } from 'lucide-react';
import { type ReactNode, useEffect, useId, useRef } from 'react';
import { Link, type LinkProps, useLocation } from 'react-router';
import { cn } from '@/lib/classnames';
import { popTopLayerHost, pushTopLayerHost } from '@/lib/toplayer';

// ─── Page header ─────────────────────────────────────────────────────────
// Consistent title block across authenticated pages.

export function PageHeader({
  title,
  description,
  actions,
  breadcrumbs,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  breadcrumbs?: ReactNode;
  className?: string;
}) {
  return (
    <header className={cn('mb-6', className)}>
      {breadcrumbs && <div className="mb-3">{breadcrumbs}</div>}
      {/* Stack on phones (explicit, not flex-wrap - a wide child like a long
          OrgPicker name keeps the row from wrapping cleanly); horizontal sm+. */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6">
        <div className="min-w-0 flex-1">
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--text-primary)', lineHeight: 1.2, wordBreak: 'break-word' }}>
            {title}
          </h1>
          {description && (
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '8px', maxWidth: '42rem', lineHeight: 1.6 }}>{description}</p>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-3 flex-wrap min-w-0 sm:shrink-0 sm:justify-end">
            {actions}
          </div>
        )}
      </div>
    </header>
  );
}

// ─── Breadcrumbs ─────────────────────────────────────────────────────────

interface Crumb {
  label: ReactNode;
  to?: string;
}

export function Breadcrumbs({ items }: { items: Crumb[] }) {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-ink-500">
      {items.map((c, i) => {
        const isLast = i === items.length - 1;
        const content = c.to && !isLast ? (
          <Link to={c.to} className="hover:text-ink-200 transition-colors">{c.label}</Link>
        ) : (
          <span className={cn(isLast && 'text-ink-300')}>{c.label}</span>
        );
        return (
          <span key={i} className="inline-flex items-center gap-1.5">
            {content}
            {!isLast && <ChevronRight className="w-3 h-3 text-ink-700" />}
          </span>
        );
      })}
    </nav>
  );
}

// ─── Skeleton ────────────────────────────────────────────────────────────

export function Skeleton({
  className,
  width,
  height,
}: {
  className?: string;
  width?: string | number;
  height?: string | number;
}) {
  const style: React.CSSProperties = {};
  if (width !== undefined) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height !== undefined) style.height = typeof height === 'number' ? `${height}px` : height;
  return <span className={cn('skeleton block', className)} style={style} aria-hidden />;
}

// Scoped route-loading fallback for lazy page chunks. Content-shaped (a
// header block + a few rows) rather than a centered spinner, so the rare
// cold-load of a pane reads as the page assembling in place instead of the
// region blanking. Lives inside the layout Suspense boundaries, so the
// surrounding chrome/rail stays put while only this pane fills in.
export function PaneFallback() {
  return (
    <div aria-busy="true" aria-live="polite">
      <div className="space-y-2.5 mb-7">
        <Skeleton height={26} className="w-44" />
        <Skeleton height={14} className="w-80 max-w-full" />
      </div>
      <div className="space-y-2">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="card" style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
            <Skeleton width={20} height={20} className="rounded shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton height={14} className="w-1/3" />
              <Skeleton height={11} className="w-2/3" />
            </div>
            <Skeleton width={72} height={26} className="rounded shrink-0" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  compact,
}: {
  icon?: LucideIcon;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
}) {
  return (
    <div className={cn('text-center', compact ? 'py-10' : 'py-16')}>
      {Icon && (
        <div className="mb-4">
          <Icon className="w-6 h-6 text-ink-400 mx-auto" />
        </div>
      )}
      <div className="text-ink-100 font-semibold tracking-tight">{title}</div>
      {description && (
        <p className="text-sm text-ink-500 mt-1.5 max-w-sm mx-auto leading-relaxed">{description}</p>
      )}
      {action && <div className="mt-5 flex items-center justify-center">{action}</div>}
    </div>
  );
}

// ─── Error state ─────────────────────────────────────────────────────────
// "Couldn't load this" counterpart to EmptyState; always offers Retry.

export function ErrorState({
  title = 'Something went wrong',
  description,
  retry,
  compact,
  detail,
}: {
  title?: ReactNode;
  description?: ReactNode;
  /** Retry handler. Provide to render a "Retry" button. */
  retry?: () => void;
  compact?: boolean;
  /** Optional technical detail rendered as a collapsible <details> block.
   *  Use sparingly - error messages from the API surface to operators,
   *  not end users. */
  detail?: string;
}) {
  return (
    <div
      className={cn('text-center', compact ? 'py-10' : 'py-16')}
      role="alert"
      aria-live="polite"
    >
      <div className="mb-4">
        <svg className="w-6 h-6 text-verdict-malicious mx-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      </div>
      <div className="text-ink-100 font-medium">{title}</div>
      {description && (
        <p className="text-sm text-ink-400 mt-1.5 max-w-sm mx-auto">{description}</p>
      )}
      {retry && (
        <div className="mt-5 flex items-center justify-center">
          <button type="button" onClick={retry} className="btn-secondary">
            Retry
          </button>
        </div>
      )}
      {detail && (
        <details className="mt-4 text-xs text-ink-500 max-w-md mx-auto">
          <summary className="cursor-pointer select-none hover:text-ink-300">Details</summary>
          <pre className="mt-2 whitespace-pre-wrap break-words text-left p-3 rounded bg-ink-900/70 border border-ink-800">{detail}</pre>
        </details>
      )}
    </div>
  );
}

// ─── Avatar ──────────────────────────────────────────────────────────────
// Initial in a circle, color deterministically hashed from the input.
// Palette is cobalt-leaning so avatars stay on-brand (one amber pair aside).

const AVATAR_GRADIENTS = [
  ['#38BDF8', '#0F5FAC'], // forge arc → cobalt (brand)
  ['#22D3EE', '#0E7490'], // cyan
  ['#818CF8', '#4338CA'], // indigo
  ['#A78BFA', '#6D28D9'], // violet
  ['#34D399', '#047857'], // emerald (subdued)
  ['#F472B6', '#9D174D'], // pink
  ['#FB923C', '#9A3412'], // amber
  ['#60A5FA', '#1E40AF'], // azure
];

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export function Avatar({
  name,
  size = 'md',
  className,
}: {
  name: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const dim = { xs: 'w-6 h-6 text-[10px]', sm: 'w-7 h-7 text-xs', md: 'w-8 h-8 text-sm', lg: 'w-10 h-10 text-base' }[size];
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  const [from, to] = AVATAR_GRADIENTS[hashStr(name || '?') % AVATAR_GRADIENTS.length];
  return (
    <div
      className={cn(
        'rounded-full flex items-center justify-center font-semibold text-white shadow-sm shrink-0',
        dim,
        className
      )}
      style={{ backgroundImage: `linear-gradient(135deg, ${from}, ${to})` }}
      aria-hidden
    >
      {initial}
    </div>
  );
}

// ─── Banner ──────────────────────────────────────────────────────────────

export function Banner({
  variant = 'info',
  icon: Icon,
  children,
  action,
}: {
  variant?: 'info' | 'success' | 'warn' | 'danger';
  icon?: LucideIcon;
  children: ReactNode;
  action?: ReactNode;
}) {
  const cls = {
    info: 'banner-info',
    success: 'banner-success',
    warn: 'banner-warn',
    danger: 'banner-danger',
  }[variant];
  return (
    <div className={cls} role="status">
      {Icon && <Icon className={cn('w-5 h-5 shrink-0 mt-0.5', {
        info: 'text-ink-400',
        success: 'text-verdict-clean',
        warn: 'text-verdict-suspicious',
        danger: 'text-verdict-malicious',
      }[variant])} />}
      <div className="flex-1 text-sm">{children}</div>
      {action}
    </div>
  );
}

// ─── Stat card ───────────────────────────────────────────────────────────
// A single big-number tile (tabular numerics so a row of them stays aligned).

export function StatCard({
  label,
  value,
  hint,
  icon: Icon,
  trend,
  sparkline,
  liveLabel,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  icon?: LucideIcon;
  trend?: { dir: 'up' | 'down' | 'neutral'; label: ReactNode };
  accent?: boolean;
  sparkline?: ReactNode;
  liveLabel?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('kpi-card card group', className)}>
      <div className="kpi-header">
        <div className="kpi-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {Icon && <Icon size={16} color="var(--text-muted)" />}
          {label}
        </div>
        {liveLabel}
      </div>
      <div className="kpi-body" style={{ justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 0 }}>
        <div className="kpi-value">{value}</div>
        {sparkline}
        {trend && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem', fontWeight: 500, color: trend.dir === 'up' ? 'var(--status-success)' : trend.dir === 'down' ? 'var(--status-danger)' : 'var(--text-muted)' }}>
            {trend.dir === 'up' && '↑'}
            {trend.dir === 'down' && '↓'}
            {trend.label}
          </div>
        )}
      </div>
      {hint && (
        <div style={{ marginTop: '12px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>{hint}</div>
      )}
    </div>
  );
}

// ─── Spinner ─────────────────────────────────────────────────────────────

export function Spinner({ size = 16, className }: { size?: number; className?: string }) {
  return <Loader2 className={cn('animate-spin text-ink-400', className)} style={{ width: size, height: size }} />;
}

// ─── Section ─────────────────────────────────────────────────────────────
//
// A titled card section. Common pattern across report + admin pages.

export function Section({
  title,
  description,
  actions,
  children,
  className,
  bodyClassName,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={cn('card', className)} style={{ padding: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>{title}</h2>
          {description && <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '4px' }}>{description}</p>}
        </div>
        {actions && <div style={{ flexShrink: 0 }}>{actions}</div>}
      </div>
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

// ─── Chip ────────────────────────────────────────────────────────────────
//
// A tag on a row. The words carry the fact and the tint is emphasis, so both
// tones read in both themes: verdict-suspicious as text measures 3.5:1 on the
// light card, which is why the warn tone tints the box and keeps the text in
// ink. Neutral is for a state that is a fact (superseded, not imported), warn
// for one the operator has to act on (no runner, gateway down).

export function Chip({
  tone = 'neutral',
  title,
  className,
  children,
}: {
  tone?: 'neutral' | 'warn';
  title?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-chip border px-1.5 py-0.5 text-mini font-medium',
        tone === 'warn'
          ? 'border-verdict-suspicious/40 bg-verdict-suspicious/[0.12] text-ink-50'
          : 'border-ink-700 bg-ink-850 text-ink-200',
        className,
      )}
    >
      {children}
    </span>
  );
}

// ─── NavItem (used by AppLayout sidebar / topbar) ────────────────────────

export function NavItem({
  to,
  icon: Icon,
  label,
  end,
  badge,
}: {
  to: LinkProps['to'];
  icon: LucideIcon;
  label: ReactNode;
  end?: boolean;
  badge?: ReactNode;
}) {
  const loc = useLocation();
  const path = typeof to === 'string' ? to : (to as { pathname: string }).pathname;
  const active = end ? loc.pathname === path : loc.pathname === path || loc.pathname.startsWith(path + '/');
  return (
    <Link
      to={to}
      className={cn(
        'ring-focus inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
        active
          ? 'bg-accent-600/15 text-accent-200'
          : 'text-ink-400 hover:text-ink-100 hover:bg-ink-900'
      )}
    >
      <Icon className="w-4 h-4" />
      {label}
      {badge && <span className="ml-1">{badge}</span>}
    </Link>
  );
}

// ─── DescriptionList ─────────────────────────────────────────────────────
//
// Compact label/value display. Used in detail pages.

export function DescriptionList({ children, className }: { children: ReactNode; className?: string }) {
  return <dl className={cn('grid grid-cols-1 sm:grid-cols-[max-content_1fr] gap-x-6 gap-y-2.5 text-sm', className)}>{children}</dl>;
}

export function DTRow({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <>
      <dt className="text-ink-500">{label}</dt>
      <dd className="text-ink-100 min-w-0 break-words">{children}</dd>
    </>
  );
}

export { Checkbox, Switch } from './Checkbox';
export { CodeBlock } from './CodeBlock';
export { type CodeTab, CodeTabs, panelId, tabId } from './CodeTabs';
export { CountryFlag, hasFlag } from './CountryFlag';
export { type Column, ResponsiveTable } from './ResponsiveTable';
export { SearchInput } from './SearchInput';
export { Segmented } from './Segmented';
export { Select, type SelectOption } from './Select';
// Sheet re-exported from its own module so callers can do
//   import { Sheet } from '@/components/ui';
export { Sheet } from './Sheet';
export { StatCell, StatStrip } from './StatStrip';

// ─── Modal ───────────────────────────────────────────────────────────────

/**
 * Modal is a centred task dialog. Forms, confirmations, anything with an
 * action. Use Sheet for a panel anchored to a viewport edge, and useConfirm
 * for a plain yes or no question.
 *
 * It is the native <dialog> element driven by showModal(), so focus
 * containment, Escape, the inert background and the dialog role all come
 * from the browser rather than from a hand-rolled focus trap. The version
 * this replaced was a plain div carrying role="dialog": nothing held focus,
 * tab walked straight out into the live page behind it, and it had no
 * accessible name.
 *
 * It does not close on a backdrop click. Every dialog here either holds
 * input or is an acknowledgement, and a backdrop handler throws the form
 * away when someone selects text in a field and releases the pointer
 * outside. The close control, Cancel and Escape are the ways out.
 *
 * To focus a field on open, mark it `data-autofocus`. React's `autoFocus`
 * does not work inside a dialog and fails silently - see the effect below.
 *
 * Controlled component. The parent owns `open`.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descId = useId();

  // The cleanup is what restores focus to whatever opened the dialog. Most
  // call sites render this as `{isOpen && <Modal open .../>}`, so closing is
  // an unmount and `open` never actually transitions to false - without
  // close() running here the element would be torn out of the top layer and
  // focus would be left on nothing.
  useEffect(() => {
    const el = ref.current;
    if (!el || !open) return;
    el.showModal();
    // React's `autoFocus` is dead inside a dialog. react-dom never writes the
    // `autofocus` content attribute - it calls .focus() itself during the
    // layout commit, which runs children before parents, so the field calls
    // focus() while this dialog is still display:none and the call is lost.
    // showModal() then finds no autofocus to honour and falls back to the
    // first focusable node, which is the close button in .modal-head. That is
    // how Enter on a destructive confirm came to hit Cancel. A data attribute
    // is what survives, because React does render it.
    el.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    // Anything that has to paint above this dialog renders into it, not into
    // <body>, so the toast stack needs to know the dialog is up.
    pushTopLayerHost(el);
    return () => {
      popTopLayerHost(el);
      el.close();
    };
  }, [open]);

  // showModal() makes the rest of the document inert but does not stop it
  // scrolling behind the dialog.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const sizeClass = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-2xl', xl: 'max-w-4xl' }[size];

  return (
    <dialog
      ref={ref}
      className={cn('modal', sizeClass)}
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={description ? descId : undefined}
      // Escape raises `cancel` before `close`. Taking it here keeps React the
      // single owner of `open`, rather than letting the element close itself
      // and drift out of sync with the parent's state.
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <div className="modal-head">
        <div className="min-w-0">
          {title && (
            <h2 id={titleId} className="text-lg font-semibold text-ink-50 tracking-tight">
              {title}
            </h2>
          )}
          {description && (
            <p id={descId} className="text-sm text-ink-400 mt-1 leading-relaxed">
              {description}
            </p>
          )}
        </div>
        <button
          type="button"
          className="btn-icon-sm -mr-1 -mt-1 shrink-0"
          onClick={onClose}
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="modal-body">{children}</div>
    </dialog>
  );
}

